import { useCallback } from "react";

/**
 * useCachedDataLoader
 */

type Props<T extends {}, TArgs extends any[]> = {
  key: string;
  loader: (...args: TArgs) => Promise<T>;
  onSet: (props: T) => void;
  onError?: (e: unknown) => void;
  onFinish?: () => void;
};

export const useCachedDataLoader = <T extends {}, TArgs extends any[],>({
  key,
  loader,
  onSet,
  onError,
  onFinish,
}: Props<T, TArgs>) => {
  return useCallback(async (...args: TArgs) => {
    const cachedValue = window.localStorage.getItem(key);
    if (typeof cachedValue === 'string') {
      try {
        onSet(JSON.parse(cachedValue) as T);

        const p = new Promise(async (accept, reject) => {
          try {
            const data = await loader(...args);
            window.localStorage.setItem(key, JSON.stringify(data));
            onSet(data);
            accept(null);
          } catch (e: unknown) {
            reject(e);
          } finally {
            onFinish?.();
          }
        });

        p.catch(e => onError?.(e));
      } catch (e: unknown) {
        try {
          const data = await loader(...args);
          window.localStorage.setItem(key, JSON.stringify(data));
          onSet(data);
        } catch (e: unknown) {
          onError?.(e);
        } finally {
          onFinish?.();
        }
      }
    } else {
      try {
        const data = await loader(...args);
        window.localStorage.setItem(key, JSON.stringify(data));
        onSet(data);
      } catch (e: unknown) {
        onError?.(e);
      } finally {
        onFinish?.();
      }
    }
  }, [
    key,
    loader,
    onSet,
    onError,
    onFinish,
  ]);
}